import * as React from "react";
import moment from "moment";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { 
  MarkSharedOfferController, 
  ViewOfferPWA, 
  useRedeemOffer, 
  useReferrerData, 
  CountClickThrough,
  usePointCard
} from "@njoy/controller";
import { maxPointsForPrice } from "@njoy/common";
import { Formik, FormikHelpers as FormikActions } from "formik";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import OfferTag from "../../shared/OfferTag";
import { TransactionSuccessfulModal } from "../../../components/modals/transactionSuccessModal";
import * as ROUTES from "../../../constants/routes";
import NewButton from "../../../components/uiMerge/newButton";
import { TemplateSub } from "../../../components/layouts/TemplateSub";
import TermsAndConditionsModal from "../../../components/modals/termsAndConditionsModal";
import OfferConfirmationModal from "../../../components/modals/offerConfirmatioModal";
import ToastMessage from "../../../hooks/useToast";
import { RedirectModal } from "../../../components/modals/redirectModal";
import PageLoader from "src/modules/shared/PageLoader";
import ShareButton from "src/components/uiMerge/shareButton";
import { openSupportEscalationMail } from "../../../utils/supportEscalation";
import { requestPushPromptForAction } from "../../../utils/pushNotifications";
import { useBrand } from "../../../hooks/useBrand";
import { useTenantConfig } from "../../../hooks/useTenantConfig";

export interface FormValues {
  id: string;
  // Points towards a paid offer's price (0 = none).
  points: number;
}

const PAID_OFFER_TYPES = ["Discount", "Cashback"];

export const ViewOfferConnector: React.FC = () => {
  const { appName } = useBrand();
  const { config: tenantConfig } = useTenantConfig();
  const { pointBalance } = usePointCard();
  const { offerId } = useParams<{ offerId: string }>();
  const { redeemOffer } = useRedeemOffer();
  const { data } = useReferrerData();
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = React.useState(false);
  const [countdown, setCountdown] = React.useState<string>("");
  const [daysLeft, setDaysLeft] = React.useState<number | null>(null);
  const [endDate, setEndDate] = React.useState<moment.Moment | null>(null);
  // New state variables for additional functionality
  const [termsModalVisible, setTermsModalVisible] = React.useState(false);
  const [offerConfirmationModalVisible, setOfferConfirmationModalVisible] =
    React.useState(false);
  const [njoyCode, setNjoyCode] = React.useState<string>("");
  const [partnerVoucherCode, setPartnerVoucherCode] = React.useState<string>("");
  const [referralCountdown, setReferralCountdown] = React.useState<number>(0);
  const [referralUrl, setReferralUrl] = React.useState<string>("");
  const [showReferralCountdown, setShowReferralCountdown] =
    React.useState(false);
  const [errorCount, setErrorCount] = React.useState(0);
  const [supportIssue, setSupportIssue] = React.useState<{
    message: string;
    errorCode?: string;
    supportRef?: string;
  } | null>(null);

  const referralIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleCancel = () => {
    setVisible(false);
    navigate(ROUTES.DISCOVER);
  };

  const showTermsModal = () => setTermsModalVisible(true);
  const handleTermsCancel = () => setTermsModalVisible(false);

  const showOfferConfirmationModal = () => setOfferConfirmationModalVisible(true);
  const handleOfferConfirmationCancel = () => setOfferConfirmationModalVisible(false);

  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleReferralRedirect = (url: string) => {
    setReferralCountdown(5);
    setReferralUrl(url);
    setShowReferralCountdown(true);

    referralIntervalRef.current = setInterval(() => {
      setReferralCountdown((prev) => {
        if (prev <= 1) {
          if (referralIntervalRef.current)
            clearInterval(referralIntervalRef.current);
          setShowReferralCountdown(false);
          window.open(url, "_blank");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const customRedeemOffer = async (
    values: FormValues,
    { setSubmitting }: FormikActions<FormValues>
  ) => {
    setSubmitting(true);
    try {
      const paramsReferralCode =
        new URLSearchParams(location.search).get("referralCode") || undefined;

      const result = await redeemOffer({
        id: values.id,
        referralCode: paramsReferralCode,
        points: values.points > 0 ? values.points : undefined,
      }) as Awaited<ReturnType<typeof redeemOffer>> & {
        partnerVoucher?: string | null;
      };
      setSubmitting(false);
      setOfferConfirmationModalVisible(false);

      // Handle network or unexpected errors
      if (!result) {
        setSupportIssue({ message: "No response from server. Please try again." });
        setErrorCount((currentCount) => currentCount + 1);
        ToastMessage("error", "No response from server. Please try again.");
        return;
      }

      // Handle business logic errors from controller
      if (result.errors && result.errors.length > 0) {
        const errorMessages = result.errors
          .filter((e: any) => e != null)
          .map((e: any) => e.message)
          .join(", ");
        const supportMeta = (result as {
          supportMeta?: {
            code?: string;
            supportRef?: string;
          };
        }).supportMeta;
        setSupportIssue({
          message: errorMessages,
          errorCode: supportMeta?.code,
          supportRef: supportMeta?.supportRef,
        });
        setErrorCount((currentCount) => currentCount + 1);
        ToastMessage("error", errorMessages);
        return;
      }

      // Handle GraphQL errors (Apollo error object)
      if ((result as any).error && (result as any).error.message) {
        setSupportIssue({ message: (result as any).error.message });
        setErrorCount((currentCount) => currentCount + 1);
        ToastMessage("error", (result as any).error.message);
        return;
      }
      
      // Handle referral redirect (if the API supports it)
      if (result.referralUrl) {
        ToastMessage("info", "Redirecting to referral offer...");
        handleReferralRedirect(result.referralUrl);

        // Save njoyCode and show success (if the API supports it)
        if (result.njoyCode) {
          setNjoyCode(result.njoyCode);
        }

        // Save partnerVoucher and show success (if the API supports it)
        if (result.partnerVoucher) {
          setPartnerVoucherCode(result.partnerVoucher);
        }

        timeoutRef.current = setTimeout(() => {
          requestPushPromptForAction("offer-redemption-success");
          setVisible(true);
        }, 4000);

        return;
      }

      // Save njoyCode and show success (if the API supports it)
      if (result.njoyCode) {
        setNjoyCode(result.njoyCode);
      }

      // Save partnerVoucher and show success (if the API supports it)
      if (result.partnerVoucher) {
        setPartnerVoucherCode(result.partnerVoucher);
      }
      
      requestPushPromptForAction("offer-redemption-success");
      setVisible(true);
      setErrorCount(0);
      setSupportIssue(null);
      ToastMessage("success", result.message || "Offer redeemed successfully!");
    } catch (err: any) {
      setSubmitting(false);
      setSupportIssue({ message: "Something went wrong. Please try again." });
      setErrorCount((currentCount) => currentCount + 1);
      ToastMessage("error", "Something went wrong. Please try again.");
    }
  };

  // Cleanup effect for intervals
  React.useEffect(() => {
    return () => {
      if (referralIntervalRef.current) {
        clearInterval(referralIntervalRef.current);
      }
    };
  }, []);

  // Countdown logic moved to top-level
  React.useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    // Only run countdown if endDate is set
    if (endDate && daysLeft !== null && daysLeft <= 7 && daysLeft >= 0) {
      interval = setInterval(() => {
        const now = moment();
        const duration = moment.duration(endDate.diff(now));
        if (duration.asMilliseconds() <= 0) {
          setCountdown("Expired");
          if (interval) clearInterval(interval);
        } else {
          const days = Math.floor(duration.asDays());
          const hours = duration.hours();
          const minutes = duration.minutes();
          const seconds = duration.seconds();
          setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s left`);
        }
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [endDate, daysLeft]);

  // Move setState logic to useEffect to avoid setState in render

  const [offerData, setOfferData] = React.useState<any>(null);

  // This effect updates offerData when data.offer changes
  // and updates endDate/daysLeft when offerData changes
  React.useEffect(() => {
    if (offerData) {
      const calculatedEndDate = moment(offerData.endDate);
      const now = moment();
      const calculatedDaysLeft = calculatedEndDate.diff(now, "days");
      setEndDate(calculatedEndDate);
      setDaysLeft(calculatedDaysLeft);
    }
  }, [offerData]);

  // This effect sets offerData when data.offer changes
  const handleOffer = React.useCallback((data: any) => {
    if (data && data.offer) setOfferData(data.offer);
  }, []);

  const handleShareOffer = React.useCallback(async () => {
    const shareUrl = new URL(window.location.href);
    if (data?.referralCode) {
      shareUrl.searchParams.set("referralCode", data.referralCode);
    }
    const shareTitle = offerData?.title || "Share Offer";
    const shareText = offerData?.title
      ? `Check out this offer on ${appName}: ${offerData.title} - By ${offerData.createdBy.admin.merchantProfile.companyName}`
      : `Check out this offer on ${appName}.`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl.toString(),
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl.toString());
        ToastMessage("success", "Offer link copied to clipboard.");
        return;
      }

      ToastMessage("info", `Share this offer: ${shareUrl.toString()}`);
    } catch (error) {
      if ((error as Error)?.name === "AbortError") {
        return;
      }

      ToastMessage("error", "Unable to share this offer right now.");
    }
  }, [data?.referralCode, offerData, appName]);

  return (
    <ViewOfferPWA offerId={offerId!}>
      {(data) => {
        handleOffer(data);
        if (!data.offer) {
          return <PageLoader />;
        }
        return (
          <TemplateSub
            page={`View Offer`}
            bgImg={data.offer?.bannerUrl ?? ""}
            icon={
              <>
                <MarkSharedOfferController>
                  {({submit, loading}) => (
                    !loading ? (
                      <ShareButton 
                        onClick={async() => {
                          await handleShareOffer();

                          if(offerId) {
                            const res = await submit({
                              id: offerId
                            });

                            if (res.errors === null) {
                              ToastMessage("success", res.message || "");
                            }
                          }
                        }} 
                      />
                    ) : (<>...</>)
                  )}
                </MarkSharedOfferController>
              </>
            }
          >
            <ToastContainer />

            <div className="w-full flex-1 h-full  pb-4">
              <div className="flex z-[101]  pl-[1rem] sticky -mt-[30px]  justify-between w-full max-w-[450px]">
                <div className="w-[100%]">
                  <div className="flex justify-between">
                    <Link
                      to={`${ROUTES.VIEW_MERCHANT}/${data.offer.createdBy.id}`}
                    >
                      <div className="rounded-full">
                        <img
                          className="rounded-full w-[71px] h-[71px] object-cover"
                          src={data.offer?.createdBy.admin?.merchantProfile?.logoUrl ?? ""}
                          alt=""
                        />
                      </div>
                    </Link>
                    <div className="mt-[3rem] pr-[1rem]">
                      <OfferTag offerType={data.offer.offerType} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 mt-3">
                    <Link
                      to={`${ROUTES.VIEW_MERCHANT}/${data.offer.createdBy.id}`}
                    >
                      <p className="text-[16px] w-[404px] text-white truncate font-semibold">
                        {data.offer.createdBy.admin?.merchantProfile?.companyName}
                      </p>
                    </Link>
                    <p className="text-[18px] text-white font-semibold">
                      {data.offer.title}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 px-[1rem] pt-2">
                <div className="w-full p-5 bg-[#1E1E1E] rounded-lg">
                  <p className="text-white mb-4 text-[14px] opacity-60 ">
                    {data.offer.description}
                  </p>
                </div>
              </div>

              <div className="mt-4 px-[1rem]">
                <div className="w-full flex justify-between items-baseline-last p-5 bg-[#1E1E1E] rounded-lg">
                  <p className="font-light text-[14px] text-[#777]">
                    Validity:
                  </p>
                  <p className="text-[14px] text-white">
                    {data.offer.startDate && (
                      <div className="text-center text-[color:var(--brand-primary)] text-sm">
                        {daysLeft !== null && daysLeft <= 7 && daysLeft >= 0 ? (
                          <span className="text-center text-[color:var(--brand-primary)] text-sm">
                            {countdown}
                          </span>
                        ) : data.offer ? (
                          moment(data.offer?.endDate).format("MMMM D, YYYY")
                        ) : (
                          "Available Now"
                        )}
                      </div>
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-6 px-[1rem] flex flex-col gap-10 mb-5">
                <div className="flex gap-5 justify-between w-full">
                  <NewButton
                    onClick={() => navigate(-1)}
                    fullWidth
                    variant="outline">
                    Back to Offers
                  </NewButton>

                  <CountClickThrough>
                    {({ countClickThrough }) => (
                      <NewButton
                        fullWidth
                        variant="primary"
                        className="w-[100%]"
                        onClick={() => {
                          countClickThrough({
                            variables: { offerId: data.offer!.id },
                          });
                          showOfferConfirmationModal();
                        }}
                      >
                        Redeem Offer
                      </NewButton>
                    )}
                  </CountClickThrough>
                </div>

                {supportIssue && errorCount >= 3 && (
                  <NewButton
                    onClick={() =>
                      openSupportEscalationMail({
                        actionName: "Redeem Offer",
                        errorMessage: supportIssue.message,
                        errorCode: supportIssue.errorCode,
                        supportRef: supportIssue.supportRef,
                        operationName: "RedeemOfferMutation",
                        extra: {
                          offerId: offerId || "",
                          referralCodePresent: Boolean(
                            new URLSearchParams(location.search).get("referralCode")
                          ),
                        },
                      })
                    }
                    variant="outline"
                    fullWidth
                  >
                    Need help with this offer? Contact Support
                  </NewButton>
                )}

                <TermsAndConditionsModal
                  visible={termsModalVisible}
                  onCancel={handleTermsCancel}
                  offerName={data.offer?.title ?? ""}
                  offerTOR={data.offer?.offerTOR ?? ""}
                />

                <p
                  onClick={showTermsModal}
                  className="text-[color:var(--brand-primary)] cursor-pointer text-center ">
                  Offer Terms & Conditions
                </p>
              </div>

              <Formik<FormValues>
                initialValues={{ id: data.offer?.id, points: 0 }}
                onSubmit={customRedeemOffer}>
                {({ isSubmitting, submitForm, values, setFieldValue }) => {
                  // Points can go towards paid offers where the site allows it.
                  const terms = tenantConfig?.pointsToCash;
                  const price = data.offer?.amount ?? 0;
                  const maxPoints =
                    terms && price > 0 && PAID_OFFER_TYPES.includes(data.offer?.offerType ?? "")
                      ? Math.min(Math.floor(pointBalance), maxPointsForPrice(price, terms))
                      : 0;
                  return (
                    <OfferConfirmationModal
                      visible={offerConfirmationModalVisible}
                      onCancel={handleOfferConfirmationCancel}
                      isSubmitting={isSubmitting}
                      onClick={submitForm}
                      offerName={data.offer?.title ?? ""}
                      offerTOR={data.offer?.offerTOR ?? ""}
                      pointsOption={
                        terms && maxPoints > 0
                          ? {
                              maxPoints,
                              pointValue: terms.pointValue,
                              price,
                              points: values.points,
                              onChange: (points) => setFieldValue("points", points),
                            }
                          : undefined
                      }
                    />
                  );
                }}
              </Formik>

              <RedirectModal
              visible={showReferralCountdown}
              referralCountdown={referralCountdown}
              referralUrl={referralUrl} />

              <TransactionSuccessfulModal
                offerType={data.offer.offerType}
                isFreebie
                njoyCode={njoyCode}
                partnerCode={partnerVoucherCode}
                title="Redemption Successful!"
                isOpen={visible}
                onClose={handleCancel}
                icon={data.offer?.createdBy.admin?.merchantProfile?.logoUrl ?? ""}
                name={data.offer?.createdBy.admin?.merchantProfile?.companyName ?? ""}
                redeemedAt={moment().format("MMMM D, YYYY")}
                redeemedTime={moment().format("h:mm A")}
              />
            </div>
          </TemplateSub>
        );
      }}
    </ViewOfferPWA>
  );
};
